<template>
  <div class="container py-10 mx-auto px-1 sm:px-0">
    <h1 class="text-5xl font-bold text-center underline underline-offset-4 mb-10">
      Shipping Your Magic Cards
    </h1>

    <div class="flex flex-col lg:flex-row gap-10 lg:items-start">
      <!-- LEFT: Dropdown content -->
      <div class="flex-1 space-y-4">
        <!-- Shipping Overview -->
        <details class="border-b pb-3">
          <summary class="cursor-pointer font-semibold">Shipping Overview</summary>
          <div class="mt-2 space-y-4 text-gray-800">
            <p>
              In this guide, you’ll find tips for <span class="font-semibold">packaging</span>,
              <span class="font-semibold">choosing carriers</span>, and other best practices to ensure
              your cards arrive safely and securely.
            </p>

            <p>Before we dive into the details, please note:</p>

            <ul class="list-disc pl-6 space-y-2">
              <li>
                <span class="font-semibold">Tracking Required</span> – Every shipment must include a tracking number.
                This helps us forecast your delivery and prepare for processing.
                You’ll receive this number when you print your shipping label.
              </li>
              <li>
                <span class="font-semibold">Insurance Recommended</span> – While we take great care once your cards arrive,
                Storm Count Exchange is <span class="font-semibold">not responsible for lost or damaged shipments</span>.
                For peace of mind, we strongly encourage adding insurance through your chosen carrier.
              </li>
            </ul>
          </div>
        </details>

        <!-- Packing Guidelines -->
        <details class="border-b pb-3">
          <summary class="cursor-pointer font-semibold">Packing Guidelines for Your Cards</summary>
          <div class="mt-2 space-y-4 text-gray-800">
            <ul class="list-disc pl-6 space-y-2">
              <li>
                <span class="font-semibold">De-Sleeving</span> – While not required, we strongly recommend removing cards
                from binders, sleeves, and pages. These add little to no protection and can actually cause surface wear
                when cards shift during transit. Because of the extra time needed to de-sleeve, shipments with 100+
                sleeved cards will incur a <span class="font-semibold">$20 de-sleeving fee per 250 cards sleeved.</span>
              </li>
              <li>
                <span class="font-semibold">Protecting Valuable Cards</span> – For high-value singles,
                <span class="font-semibold">top-loaders or team bags</span> (with a foldable adhesive flap) are best.
                Please <span class="font-semibold">do not tape the tops</span> of top-loaders.
              </li>
              <li>
                <span class="font-semibold">Card Containers</span> – Secure your cards inside a
                <span class="font-semibold">deck box, bundle box, hard case, or single-row card box</span> before packing.
              </li>
              <li>
                <span class="font-semibold">Avoid Unsafe Fasteners</span> – <span class="font-semibold">Do not</span> use tape,
                rubber bands, paper clips, or similar items on loose cards or team bags, as these can easily cause damage in transit.
                <span class="font-semibold">Do not</span> use newspaper to wrap cards in (ink transfer is a risk).
              </li>
              <li>
                <span class="font-semibold">Tight Packing</span> – Fill your shipping box so cards and containers can’t shift around.
                Extra space leads to extra movement—and possible damage. Extra basic lands work great!
              </li>
              <li>
                <span class="font-semibold">Cushioning Materials</span> – Use
                <span class="font-semibold">packing paper, bubble wrap, or peanuts</span> to reduce shifting inside the package.
              </li>
              <li>
                <span class="font-semibold">Shipping Sealed Product</span> –
                Do not apply tape directly to sealed boxes. It can damage packaging when removed.
              </li>
            </ul>
          </div>
        </details>

        <!-- Shipping Containers -->
        <details class="border-b pb-3">
          <summary class="cursor-pointer font-semibold">Shipping Containers for Your Cards</summary>
          <div class="mt-2 space-y-4 text-gray-800">
            <p>
              These recommendations are based on our experience to help make the shipping process smoother.
              However, <span class="font-semibold">Storm Count Exchange does not guarantee or warrant</span>
              the adequacy of these products. It is always your responsibility to ensure (and insure!) your
              collection arrives safely and in the condition it was sent.
            </p>

            <!-- Box Quality -->
            <div>
              <h4 class="font-semibold">Box Quality: The Edge Crush Test (ECT) Rating</h4>
              <ul class="list-disc pl-6 space-y-1">
                <li>Choose a <span class="font-semibold">heavy-duty shipping box</span> with reinforced walls.</li>
                <li>
                  Look for an <span class="font-semibold">ECT rating of 32 lbs or 200C</span>, which means the box can
                  withstand more pressure and weight in transit.
                </li>
                <li>❌ Avoid thin, brown USPS moving boxes — they often crush or split under pressure.</li>
              </ul>
            </div>

            <!-- Card Storage Boxes -->
            <div>
              <h4 class="font-semibold">Card Storage Boxes</h4>
              <ul class="list-disc pl-6 space-y-1">
                <li>
                  <span class="font-semibold">Recommended Products:</span> Standard card storage boxes are ideal.
                  Smaller ones can be shipped inside a larger outer box with padding for extra security.
                </li>
                <li>
                  <span class="font-semibold">Best Practice:</span> Always wrap card storage boxes in bubble wrap or packing paper
                  and place them inside a larger shipping box.
                </li>
                <li>
                  <span class="font-semibold">Availability:</span> These boxes can usually be purchased at local game/comic shops
                  or online retailers like Amazon.
                </li>
                <li>
                  <span class="font-semibold">Capacity Guidelines:</span>
                  <ul class="list-disc pl-6 space-y-1">
                    <li>
                      <a href="https://www.amazon.com/BCW-Cardboard-Organizer-Basketball-Protection/dp/B00H5HC9G4/" target="_blank" class="text-blue-600 underline">
                        Up to 400 cards
                      </a> → use a single 400 count box
                    </li>
                    <li>
                      <a href="https://www.amazon.com/Ct-800-Card-Storage-Gaming/dp/B00MWJQ7X6/" target="_blank" class="text-blue-600 underline">
                        Up to 800 cards
                      </a> → use a single 800 count box
                    </li>
                    <li>For larger shipments, combine multiple 400 or 800 ct boxes.</li>
                    <li>
                      USPS Medium Flat Rate Boxes can fit up to
                      <span class="font-semibold">4 × 400 ct storage boxes</span>
                      (free at USPS locations or
                      <a href="https://tools.usps.com/find-location.htm?" target="_blank" class="text-blue-600 underline">online</a>).
                    </li>
                    <li>
                      For very small shipments you may use a <span class="font-semibold">deck box</span> inside a Poly or Kraft
                      Bubble Mailer (available at any office supply store).
                    </li>
                  </ul>
                </li>
              </ul>
            </div>

            <!-- Bubble Wrap & Filler -->
            <div>
              <h4 class="font-semibold">Bubble Wrap & Filler</h4>
              <ul class="list-disc pl-6 space-y-1">
                <li>
                  Use <span class="font-semibold">packing peanuts, bubble wrap, or packing paper</span> to fill empty space and
                  prevent shifting.
                </li>
                <li>Wrap individual card boxes inside your main shipping box for an extra layer of protection.</li>
                <li>❌ <span class="font-semibold">Do not</span> use newspaper — ink can transfer and damage your cards.</li>
                <li>Pro tip: Save bubble wrap from your own shipments, or pick some up locally for under $5.</li>
                <li>
                  Need to order some
                  <a href="https://www.amazon.com/WANGUAGUA-Shipping-Cushioning-Supplies-Perforated/dp/B0BR5DMP3Z/" target="_blank" class="text-blue-600 underline">Bubble Wrap?</a>
                </li>
              </ul>
            </div>
          </div>
        </details>

        <!-- Choosing a Shipping Carrier -->
        <details class="border-b pb-3">
          <summary class="cursor-pointer font-semibold">Choosing a Shipping Carrier</summary>
          <div class="mt-2 space-y-4 text-gray-800">
            <p>
              We've listed some of the major carriers below, with the typical cost you can expect.
              The ideal carrier often varies based on the size of your shipment: For example,
              USPS flat shipping rates are great for shipments up to ~4,300 cards.
              Larger shipments may get a better rate with UPS.
            </p>

            <!-- USPS -->
            <div>
              <h4 class="font-semibold">USPS</h4>
              <ul class="list-disc pl-6 space-y-1">
                <li><span class="font-semibold">No packing services</span> are provided.</li>
                <li>Rates as low as $7.35 for a 1lb box.</li>
                <li>
                  Many collections fit in a Medium <span class="font-semibold">Flat Rate Box</span>
                  (up to 25 lbs / ~6,425 cards).
                  <span class="italic">Storm Count Exchange restricts shipments to 3,200 cards per box.</span>
                </li>
                <li>
                  At any USPS location, you can get a free Medium Flat Rate box, which will fit ~a full shipment plus adequate
                  packing material for $16.65 postage.
                </li>
                <li>
                  Additional Flat Rate Box options can be ordered free from
                  <a href="https://www.usps.com/" target="_blank" class="text-blue-600 underline">USPS.com</a>.
                  Delivery times for these may vary depending on location and season.
                </li>
                <li>#1 choice – Storm Count Exchange Approved!</li>
              </ul>
            </div>

            <!-- UPS -->
            <div>
              <h4 class="font-semibold">UPS</h4>
              <ul class="list-disc pl-6 space-y-1">
                <li>Retail office locations offer packing services and materials.</li>
                <li>Rates as low as $8.95.</li>
                <li>Must bring or purchase your own box.</li>
              </ul>
            </div>

            <!-- FedEx -->
            <div>
              <h4 class="font-semibold">FedEx</h4>
              <ul class="list-disc pl-6 space-y-1">
                <li>Retail office locations offer packing services and materials.</li>
                <li>Rates as low as $9.72.</li>
                <li>We encourage you to use USPS or UPS.</li>
              </ul>
            </div>

            <!-- Commented out support line (keep for later) -->
            <!-- <p class="mt-4 font-semibold">Support@StormCountExchange.com</p> -->
          </div>
        </details>
      </div>

      <!-- RIGHT: Graphic -->
      <div class="lg:w-1/3 lg:sticky lg:top-10 flex items-start justify-center shrink-0">
        <img
          src="/assets/img/collection-size.png"
          alt="Assessing Your Collection Size"
          class="w-full max-w-md h-auto object-contain self-start rounded shadow"
        />
      </div>


    </div>
  </div>
</template>

<script setup>
// no script needed yet
</script>
